import React, { useCallback, useEffect, useState } from "react";
import ReactFlow, {
  Controls,
  Background,
  applyNodeChanges,
  OnNodesChange,
  Node,
} from "reactflow";
import styles from "./EnsembleModelsDependency.module.scss";

import "reactflow/dist/style.css";
import "./dependency.scss";
import CustomNode from "./CustomNodes";
import { getEnsembleModelRelation } from "../../services/ensembleModelling.service";

const nodeTypes = { customNode: CustomNode };
const BASE_EDGE_STYLE = {
  stroke: "#4f5c70",
  strokeWidth: 2.2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  opacity: 0.9,
};
const ACTIVE_EDGE_STYLE = {
  ...BASE_EDGE_STYLE,
  stroke: "#03234a",
  strokeWidth: 2.6,
};

const EnsembleModelsDependency = (props: any) => {
  const {
    showJobTaskPage,
    setShowJobTaskPage,
    data,
    modalDependencyChange,
    setModalDependecyChange,
    onEmsembleModelDataChange,
    setEnsembleModelSelectedNode,
    setSelectedCusdtomPlugInId,
  } = props;
  const initialNodes: Node[] = [];
  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState([]);
  const [clickedNode, setClickedNode] = useState<any>(null);
  const [addedNodes, setAddedNodes] = useState([]);

  useEffect(() => {
    let updatedEdges: any[] = [];

    getEnsembleModelRelation()
      .then((res: any) => {
        updatedEdges = res.map((value: any) => ({
          id: value.ensemble_model_relation_id,
          source: `${value.src_ensemble_model_id}`,
          target: `${value.tgt_ensemble_model_id}`,
          type: "smoothstep",
          style: BASE_EDGE_STYLE,
        }));
        setEdges(updatedEdges);
      })
      .catch(() => {
        setEdges([]);
      });
  }, []);

  useEffect(() => {
    let updatedNodes: any[] = [];
    let maxObject;
    let maxY = Number.MIN_SAFE_INTEGER;

    for (const obj of data) {
      if (obj?.canvas_position?.x === 100 && obj?.canvas_position?.y > maxY) {
        maxY = obj.canvas_position.y;
        maxObject = obj;
      }
    }
    let counter = maxObject ? maxObject.canvas_position.y : -100;
    let sortedData = [...data].sort((a, b) => a.canvas_position.x - b.canvas_position.x);

    updatedNodes = sortedData.map((value: any, index: number, dataArray: any) => {
      function isObjectEmpty(obj: any) {
        for (let key in obj) {
          if (obj.hasOwnProperty(key)) {
            return false;
          }
        }
        return true;
      }

      function areNodesOverlapping(obj1: any, obj2: any) {
        const deltaY = Math.abs(obj1.y - obj2.y);
        const deltaX = Math.abs(obj1.x - obj2.x);
        if (deltaX < 40 && deltaY < 60) {
          return { x: obj1.x, y: obj1.y + 100 };
        }
        return obj1;
      }

      return {
        id: `${value.ensemble_model_id}`,
        position: !isObjectEmpty(value.canvas_position)
          ? areNodesOverlapping(
              value.canvas_position,
              dataArray[index + 1]?.canvas_position || value.canvas_position
            )
          : { x: 100, y: (counter += 100) },

        data: {
          label: value.ensemble_model_name,
          subject_area: value.subject_area,
          modelId: value.ensemble_model_id,
        },
        type: "customNode",
      };
    });
    setNodes(updatedNodes);
  }, [data]);

  useEffect(() => {
    modalDependencyChange &&
      onEmsembleModelDataChange({
        addedNodes: addedNodes,
        nodes: nodes,
        edges: edges,
      });
  }, [nodes, edges]);

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [setNodes]
  );

  const onNodeDragStart = (
    event: React.MouseEvent,
    node: Node,
    nodes: Node[]
  ) => {
    onNodeClick(null, node);
    setModalDependecyChange(true);
  };

  const onNodeClick = (event: React.MouseEvent, element: any) => {
    setEnsembleModelSelectedNode(element);
    setAddedNodes((pre: any) => [...pre, element.id]);
    const clickedNode = nodes.find(
      (el) => el.data.label === element.data.label
    );
    setClickedNode(clickedNode);
    setSelectedCusdtomPlugInId(parseInt(element.id));

    if (element?.id && element.type === "customNode") {
      const updatedElements = edges.map((el) => {
        if (el.source === element.id || el.target === element.id) {
          return {
            ...el,
            style: ACTIVE_EDGE_STYLE,
          };
        }
        return {
          ...el,
          style: BASE_EDGE_STYLE,
        };
      });
      setEdges(updatedElements);
    }
  };

  const onConnect = (params: any) => {
    if (params.source === params.target) {
      return;
    }
    setModalDependecyChange(true);
    const sourceNode = nodes.filter((value) => value.id == params.source);
    const targetNode = nodes.filter((value) => value.id == params.target);
    setClickedNode(sourceNode[0]);
    setEnsembleModelSelectedNode(sourceNode[0]);
    setAddedNodes((pre) => [...pre, targetNode[0].id]);
    const id1 = `${sourceNode[0].id}-${targetNode[0].id}`;
    const newEdge = {
      id: id1,
      source: sourceNode[0].id,
      target: targetNode[0].id,
      type: "smoothstep",
      style: BASE_EDGE_STYLE,
    };
    const existingIndex = edges.findIndex((item) => item.id === newEdge.id);
    if (existingIndex !== -1) {
      const updatedEdges = [...edges];
      updatedEdges[existingIndex] = newEdge;
      setEdges(updatedEdges);
    } else {
      setEdges((pre) => [...pre, newEdge]);
    }
  };

  return (
    <>
      <div className={styles.mainContainer}>
        <div className={styles.reactFlow}>
          <ReactFlow
            fitView
            maxZoom={1}
            nodes={nodes.map((element) => ({
              ...element,
              data: {
                ...element.data,
                isSelected: element.id === clickedNode?.id,
                setShowJobTaskPage: setShowJobTaskPage,
                showJobTaskPage: showJobTaskPage,
                setModalDependecyChange: setModalDependecyChange,
              },
            }))}
            edges={edges}
            onNodesChange={onNodesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            onNodeClick={onNodeClick}
            connectionLineStyle={BASE_EDGE_STYLE}
            snapToGrid={true}
            snapGrid={[20, 20]}
            onNodeDragStart={onNodeDragStart}
            zoomOnDoubleClick={false}
            panOnScroll
            zoomOnScroll={false}
          >
            <Background color="#d8e4f8" gap={32} />
            <Controls />
          </ReactFlow>
        </div>
      </div>
    </>
  );
};

export default EnsembleModelsDependency;
